/**
 * ClientInfoCard Component
 * Displays selected client information, classifications, statistics, and contact options
 * Only handles UI rendering and user interactions - all logic is in lib/ modules
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, MessageSquare, Mail, PersonStanding, ClipboardList, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useClient } from '@/context/client-context';
import { usePlan } from '@/context/plan-context';
import { PlanManagementDrawer } from '@/components/drill-library/PlanManagementDrawer';
import { PlannerIcon } from '@/components/shared/Icons';
import { HeightDisplay, WeightDisplay } from '@/components/ui/unit-display';
import { calculateBMI, getBMICategory, getBMICategoryColor } from '@/components/clients/lib/bmi-calculator';
import { getPhaseClassificationData } from '@/components/drill-library/lib/phase-classification';
import { formatPhoneInternational, safeToDate } from '@/lib/utils';
import { useClientDetail } from '@/context/clients-cache-context';
import { extractClientStatistics, formatClassification } from './lib/transforms';
import { formatDateForDisplay } from './lib/calculations';
import type { ClientInfoCardProps } from './lib/types';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import logger from '@/lib/logging';

export const ClientInfoCard: React.FC<ClientInfoCardProps> = ({ client, onNavigateToClient }) => {
  const navigate = useNavigate();
  const { setClient } = useClient();
  const { activePlan } = usePlan();
  const {
    assessments: detailAssessments,
    postureAnalyses: detailPostureAnalyses,
    loading: detailLoading,
  } = useClientDetail(client?.id);

  const [isPlanDrawerOpen, setIsPlanDrawerOpen] = useState(false);
  const [isContactPopoverOpen, setIsContactPopoverOpen] = useState(false);

  const sortedDetail = useMemo(() => {
    const postureAnalyses = [...detailPostureAnalyses].sort((a, b) => {
      const dateA = safeToDate(a.createdAt)?.getTime() ?? 0;
      const dateB = safeToDate(b.createdAt)?.getTime() ?? 0;
      return dateB - dateA;
    });
    const assessments = [...detailAssessments].sort((a, b) => {
      const dateA = safeToDate(a.date)?.getTime() ?? safeToDate(a.createdAt)?.getTime() ?? 0;
      const dateB = safeToDate(b.date)?.getTime() ?? safeToDate(b.createdAt)?.getTime() ?? 0;
      return dateB - dateA;
    });
    return { postureAnalyses, assessments };
  }, [detailAssessments, detailPostureAnalyses]);

  const clientData = useMemo(() => {
    if (!client) return null;

    const stats = extractClientStatistics(
      sortedDetail.postureAnalyses,
      sortedDetail.assessments
    );

    return {
      statistics: stats,
      data: sortedDetail,
    };
  }, [client, sortedDetail]);

  // Handle classification separately as it might be async
  const [formattedClassification, setFormattedClassification] = useState<any>({
    displayText: 'No classification available',
    tags: []
  });

  useEffect(() => {
    if (client && clientData?.data) {
      formatClassification(client, clientData.data.postureAnalyses, clientData.data.assessments)
        .then(setFormattedClassification)
        .catch(err => logger.error('Error formatting classification:', err));
    }
  }, [client, clientData?.data]);

  const loading = detailLoading;
  const error = null; // Error handling is now simplified/centralized

  const handleClientClick = () => {
    if (onNavigateToClient) {
      onNavigateToClient();
    }
  };

  const handleDeactivateClient = () => {
    setClient(null);
    navigate('/clients');
  };

  if (loading) {
    return (
      <Card className="mb-4 sm:mb-6">
        <CardHeader>
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-4 sm:mb-6">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!clientData) {
    return null;
  }

  const { statistics } = clientData;
  const hasPhone = !!client.phone;
  const hasEmail = !!client.email;
  const displayLabel = clientDisplayLabel(client);

  // Calculate BMI if we have both height and weight
  const bmi = client?.height && client?.weight ? calculateBMI(client.height, client.weight) : 0;
  const bmiCategory = bmi > 0 ? getBMICategory(bmi) : '';
  const bmiColor = bmi > 0 ? getBMICategoryColor(bmi) : '';

  // Get phase classification (placeholder - would come from assessment data in real implementation)
  const phaseData = getPhaseClassificationData('Not Assessed');

  return (
    <Card className="mb-4 sm:mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/clients/${client.id}`}
                onClick={handleClientClick}
                className="text-lg sm:text-xl font-semibold hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded block truncate"
                aria-label={`View ${displayLabel} details`}
              >
                {displayLabel}
              </Link>
            </div>
            {formattedClassification.displayText !== 'No classification available' && (
              <div className="mt-1.5">
                <Badge variant="secondary" className="text-xs">
                  {formattedClassification.displayText}
                </Badge>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Plan Selection Button */}
            <PlanManagementDrawer 
              isOpen={isPlanDrawerOpen} 
              onClose={() => setIsPlanDrawerOpen(false)} 
            />
            <Button
              variant={activePlan ? "default" : "outline"}
              size="icon"
              className={`h-9 w-9 ${activePlan ? 'shadow-lg shadow-primary/30' : ''}`}
              onClick={() => setIsPlanDrawerOpen(true)}
              aria-label="Select plan"
              title={activePlan ? activePlan.name : "Select Plan"}
            >
              <span className="h-4 w-4 inline-block"><PlannerIcon /></span>
            </Button>
            
            {/* Deactivate Client Button */}
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={handleDeactivateClient}
              aria-label="Deactivate client"
              title="Deactivate client"
            >
              <X className="h-4 w-4" />
            </Button>
            
            {/* Contact Button */}
            {(hasPhone || hasEmail) && (
              <Popover open={isContactPopoverOpen} onOpenChange={setIsContactPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    aria-label={`Contact ${displayLabel}`}
                  >
                    <Phone className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <div className="flex flex-col gap-1">
                    {hasPhone && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsContactPopoverOpen(false);
                            setTimeout(() => {
                              window.location.href = `tel:${formatPhoneInternational(client.phone)}`;
                            }, 100);
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm h-11 touch-target-min w-full text-left"
                        >
                          <Phone className="h-4 w-4" />
                          <span>Call</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsContactPopoverOpen(false);
                            // Use a hidden anchor and click it programmatically for better reliability
                            const link = document.createElement('a');
                            link.href = `sms:${formatPhoneInternational(client.phone)}`;
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm h-11 touch-target-min w-full text-left"
                        >
                          <MessageSquare className="h-4 w-4" />
                          <span>Text</span>
                        </button>
                      </>
                    )}
                    {hasEmail && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsContactPopoverOpen(false);
                          // Use a hidden anchor and click it programmatically for better reliability
                          const subject = encodeURIComponent(`Re: ${displayLabel}`);
                          const link = document.createElement('a');
                          link.href = `mailto:${client.email}?subject=${subject}`;
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm h-11 touch-target-min w-full text-left"
                      >
                        <Mail className="h-4 w-4" />
                        <span>Email</span>
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Biometrics Section */}
        {(client.height || client.weight) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 pb-4 border-b">
            {/* Height */}
            {client.height && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-muted-foreground">Height</span>
                <HeightDisplay 
                  value={client.height} 
                  className="text-base sm:text-lg font-semibold"
                  precision={0}
                />
              </div>
            )}

            {/* Weight */}
            {client.weight && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-muted-foreground">Weight</span>
                <WeightDisplay 
                  value={client.weight} 
                  className="text-base sm:text-lg font-semibold"
                  precision={1}
                />
              </div>
            )}

            {/* BMI */}
            {bmi > 0 && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-muted-foreground">BMI</span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-base sm:text-lg font-semibold ${bmiColor}`}>
                    {bmi.toFixed(1)}
                  </span>
                  <Badge variant="outline" className="text-xs px-1.5 py-0 h-5">
                    {bmiCategory}
                  </Badge>
                </div>
              </div>
            )}

            {/* Phase */}
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-muted-foreground">Phase</span>
              <Badge 
                variant="outline" 
                className={`text-xs px-2 py-0.5 h-6 w-fit ${phaseData.bgColor} ${phaseData.color} border-0`}
              >
                {phaseData.phase}
              </Badge>
            </div>
          </div>
        )}

        {/* Statistics Section */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <PersonStanding className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
              <span className="text-xs sm:text-sm font-medium">Posture Analyses</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.postureCount}</div>
            {statistics.postureLastDate && (
              <p className="text-xs text-muted-foreground">
                Last: {formatDateForDisplay(statistics.postureLastDate)}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
              <span className="text-xs sm:text-sm font-medium">SOP Assessments</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.assessmentCount}</div>
            {statistics.assessmentLastDate && (
              <p className="text-xs text-muted-foreground">
                Last: {formatDateForDisplay(statistics.assessmentLastDate)}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
export default ClientInfoCard;

